/**
 * Candado del gestor (reescrito 2026-10-06 a partir del recuperado del 2026-10-05).
 *
 * Si ya se sabe cuántos dígitos tiene el PIN (estado.pinLargo), se abre solo al
 * completarlo: sin botón. Si es incorrecto, avisa, se limpia y vuelve el foco.
 * La primera vez (o si aún no se sabe el largo) se abre con Enter.
 * Elegir un PIN nuevo sigue pidiendo repetirlo.
 */
import "./gestor.css";
import {
  rec_AccionAsync as AccionAsync,
  rec_Lo as claseEntrada,
  rec_React as React,
  rec_api as api,
  rec_oi as IconoCandado,
} from "../../runtime/index.jsx";

// runtime/index.jsx importa este archivo antes de definir React (import circular).
const useEffect = (...a) => React.useEffect(...a);
const useRef = (...a) => React.useRef(...a);
const useState = (...a) => React.useState(...a);

const soloDigitos = (v) => v.replace(/\D/g, ``).slice(0, 12);

export function CandadoGestor({ estado, onAbierto }) {
  const nuevo = !estado.pinConfigurado;
  const largo = estado.pinLargo ?? null;
  const [pin, setPin] = useState(``);
  const [repetido, setRepetido] = useState(``);
  const [error, setError] = useState(``);
  const [abriendo, setAbriendo] = useState(false);
  const [sacudir, setSacudir] = useState(0);
  const campo = useRef(null);

  async function abrir(valor) {
    if (abriendo) return;
    setAbriendo(true);
    setError(``);
    try {
      const r = await api(nuevo ? `/gestor/pin` : `/gestor/abrir`, {
        method: `POST`, body: { pin: valor }, clave: `gestor-candado-${Date.now()}`,
      });
      setPin(``); setRepetido(``);
      onAbierto(r.llave);
    } catch (e) {
      setPin(``);
      setError(e.message || `No se pudo abrir.`);
      setSacudir((n) => n + 1);
      setAbriendo(false);
    }
  }

  // Vuelve el foco después de un error (el campo estuvo deshabilitado).
  useEffect(() => { if (!abriendo) campo.current?.focus(); }, [abriendo, sacudir]);

  const cambiar = (e) => {
    const v = soloDigitos(e.target.value);
    setPin(v);
    if (error) setError(``);
    if (!nuevo && largo && v.length === largo) abrir(v);
  };

  const valido = /^\d{6,12}$/.test(pin) && (!nuevo || pin === repetido);
  const enviar = (e) => { e.preventDefault(); if (valido) abrir(pin); };

  return (
    <form className="gx gx-candado" onSubmit={enviar}>
      <div className="gx-candado-cab">
        <IconoCandado className="h-8 w-8 text-(--accent-texto)" />
        <h2>{nuevo ? `Elige tu PIN` : `Tu WhatsApp está con candado`}</h2>
        <p className="gx-texto">
          {nuevo ? `De 6 a 12 números. Lo pide cada vez que entras; guárdalo bien.`
            : largo ? `Escribe tu PIN: se abre solo al completarlo.`
            : `Escribe tu PIN y presiona Enter.`}
        </p>
      </div>

      <div key={sacudir} className={`gx-candado-campo${sacudir ? ` gx-sacude` : ``}`}>
        <input
          ref={campo}
          aria-label="PIN"
          aria-invalid={Boolean(error)}
          className={`${claseEntrada} min-h-11 text-center tracking-[0.4em]`}
          type="password"
          inputMode="numeric"
          autoComplete="off"
          maxLength={12}
          autoFocus
          disabled={abriendo}
          value={pin}
          onChange={cambiar}
        />
        {!nuevo && largo && (
          <div className="gx-puntos" aria-hidden="true">
            {Array.from({ length: largo }, (_, i) => <i key={i} className={i < pin.length ? `lleno` : ``} />)}
          </div>
        )}
      </div>

      {nuevo && (
        <input
          aria-label="Repite el PIN"
          className={`${claseEntrada} min-h-11 text-center tracking-[0.4em]`}
          type="password" inputMode="numeric" autoComplete="off" maxLength={12}
          placeholder="Repítelo" value={repetido}
          onChange={(e) => setRepetido(soloDigitos(e.target.value))}
        />
      )}

      {error && <p role="alert" className="gx-candado-error">{error}</p>}
      {abriendo && !error && <p className="gx-texto" role="status">Abriendo…</p>}

      {(nuevo || !largo) && (
        <AccionAsync className="min-h-11" disabled={!valido || abriendo} onClick={() => abrir(pin)}>
          {nuevo ? `Guardar y abrir` : `Abrir`}
        </AccionAsync>
      )}
    </form>
  );
}
